import { useInfiniteQuery } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { InvoicesViewApiAdapter, InvoiceShipmentsFilters } from '../adapters/InvoicesViewApiAdapter';
import { InvoiceShipment } from '../models/InvoicesView';

interface UseInvoiceShipmentsProps {
  invoiceId: number;
  searchFilter?: { filters: any[]; logic: string };
  search?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  enabled?: boolean;
}

/**
 * useInvoicesView - Hook para cargar shipments asociados a una factura
 *
 * Utiliza React Query (useInfiniteQuery) para manejar la carga paginada de shipments
 * desde el endpoint /invoices/invoices/{id}/shipments con soporte para:
 * - Infinite scroll
 * - Filtros avanzados
 * - Sorting
 * - Cache automático
 */
export const useInvoicesView = ({
  invoiceId,
  searchFilter,
  search,
  sortBy = 'id',
  sortDirection = 'desc',
  enabled = true,
}: UseInvoiceShipmentsProps) => {
  const invoiceNumericId = Number(invoiceId);
  const isEnabled = enabled && Number.isFinite(invoiceNumericId) && invoiceNumericId > 0;

  const httpClient = createHttpClient(apiClientWH);
  const invoicesViewAdapter = new InvoicesViewApiAdapter(httpClient);

  const filters: InvoiceShipmentsFilters = {
    invoiceId: invoiceNumericId,
    searchFilter,
    search,
    sortBy,
    sortDirection,
  };

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['invoiceShipments', filters],
    queryFn: ({ pageParam = 1 }) =>
      invoicesViewAdapter.getInvoiceShipmentsPaginated({
        ...filters,
        page: pageParam,
        limit: 25,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    staleTime: 2 * 60 * 1000, // 2 minutos
    gcTime: 5 * 60 * 1000, // 5 minutos
    enabled: isEnabled,
    placeholderData: (previousData) => previousData,
  });

  const shipments: InvoiceShipment[] =
    data?.pages.flatMap((page) => page.shipments) ?? [];

  const total = data?.pages[0]?.total ?? 0;

  return {
    shipments,
    isLoading,
    isFetching,
    error: error ? (error as Error).message : null,
    total,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};
